import { flash, FormGroup, Input, Popover, Spinner } from "@xola/ui-kit";
import clsx from "clsx";
import { isNil } from "lodash";
import React, { useState } from "react";
import { ColorResult } from "react-color";
import { useMutation } from "react-query";
import { EmbeddedTheme, ThemePreference, ThemeVariablesName } from "types";
import { RadioInput } from "../../Common/RadioInput";
import ColorPicker from "./ColorPicker";
import { api } from "@/api/api";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { hasX2CheckoutWithFullProbability } from "@/models/Seller";

const DEFAULT_V1_ACCENT_COLOR = "#337AB7";
const DEFAULT_X2_ACCENT_COLOR = "#1352C6";
const DEFAULT_THEME = EmbeddedTheme.AUTO;

export const CheckoutStyling = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const checkoutPreferences = seller.preferences?.checkout;
    const hasX2Checkout = hasX2CheckoutWithFullProbability();
    const defaultAccentColor = hasX2Checkout ? DEFAULT_X2_ACCENT_COLOR : DEFAULT_V1_ACCENT_COLOR;

    let themeName = DEFAULT_THEME;
    let colorVariables = { name: ThemeVariablesName.ACCENT_COLOR, value: defaultAccentColor };
    if (checkoutPreferences?.theme) {
        themeName = checkoutPreferences.theme.name ?? DEFAULT_THEME;
        const accentColorVariables = checkoutPreferences.theme.themeVariables.find((theme) => {
            return theme.name === ThemeVariablesName.ACCENT_COLOR;
        });
        if (accentColorVariables && accentColorVariables.value !== defaultAccentColor) {
            colorVariables = accentColorVariables;
        }
    }

    const colorPicker = useModal(false);
    const [theme, setTheme] = useState(themeName ?? DEFAULT_THEME);
    const [hexColor, setHexColor] = useState(colorVariables?.value ?? defaultAccentColor);

    const handleThemeUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTheme(e.target.value as EmbeddedTheme);
        uploadCheckout(e.target.value as EmbeddedTheme);
    };

    const { mutateAsync: uploadCheckout, isLoading } = useMutation(async (newTheme: EmbeddedTheme) => {
        const payload: { theme: Partial<ThemePreference> } = {
            theme: {
                name: newTheme,
                themeUrl: undefined,
                // If the seller has not set an accent color we set it empty so that the default accent color does not
                // override the accent color from the checkout theme
                themeVariables:
                    hexColor === defaultAccentColor
                        ? []
                        : [{ name: ThemeVariablesName.ACCENT_COLOR, value: hexColor.toUpperCase() }],
            },
        };

        if (!hasX2Checkout) {
            payload.theme.themeUrl = `themes/${theme}.less?timestamp=${Math.floor(Date.now() / 1000)}`;
        }

        await api.put(`/users/${seller.id}/preferences/checkout`, payload);
        colorPicker.hide();

        flash.success("Updated checkout styling preferences");
        invalidate("sellers", seller.id);
    });

    const setColor = (hex: string | undefined): void => {
        const color = isNil(hex) ? defaultAccentColor : hex.toUpperCase();
        setHexColor(color);
    };

    const handleColorReset = (): void => {
        setHexColor(defaultAccentColor);
    };

    return (
        <div className="flex flex-1 flex-col space-y-4">
            <div className={clsx("flex flex-col space-y-2")}>
                <div className="text-md font-bold text-black">
                    Checkout Theme
                    {isLoading && <Spinner size="tiny" className="ml-1" />}
                </div>
                <RadioInput
                    name={EmbeddedTheme.LIGHT}
                    checked={theme === EmbeddedTheme.LIGHT}
                    value={EmbeddedTheme.LIGHT}
                    onChange={handleThemeUpdate}
                >
                    Light theme
                </RadioInput>
                <RadioInput
                    name={EmbeddedTheme.DARK}
                    checked={theme === EmbeddedTheme.DARK}
                    value={EmbeddedTheme.DARK}
                    onChange={handleThemeUpdate}
                >
                    Dark theme
                </RadioInput>
                <div>
                    <RadioInput
                        name={EmbeddedTheme.AUTO}
                        checked={theme === EmbeddedTheme.AUTO}
                        value={EmbeddedTheme.AUTO}
                        onChange={handleThemeUpdate}
                    >
                        Automatic based on your website
                    </RadioInput>
                </div>
            </div>
            <div className="flex flex-1 flex-col space-y-2">
                <div className="text-md font-bold text-black">Accent Color</div>
                <FormGroup className="flex items-center space-x-1">
                    <Popover
                        visible={colorPicker.isVisible}
                        maxWidth={300}
                        appendTo={document.body}
                        placement="top"
                        onClickOutside={colorPicker.hide}
                    >
                        <div
                            className="h-9 w-9 rounded"
                            style={{ backgroundColor: hexColor }}
                            onClick={colorPicker.show}
                        />
                        <Popover.Content>
                            <ColorPicker
                                color={hexColor}
                                isLoading={isLoading}
                                onChange={(e: ColorResult) => setColor(e.hex)}
                                onInputChange={(e: { hex: string }) => setColor(e.hex)}
                                onUpdate={async () => uploadCheckout(theme)}
                                onReset={handleColorReset}
                            />
                        </Popover.Content>
                    </Popover>
                    <div className="w-25">
                        <Input
                            value={hexColor}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setColor(e.target.value)}
                            onClick={colorPicker.show}
                        />
                    </div>
                </FormGroup>
            </div>
        </div>
    );
};
